import { apiClient } from "@/shared/services/api";

export type SowingBreakdownSource = "customer" | "standalone";

export interface SowingBreakdownFilters {
  startDate?: string;
  endDate?: string;
  customerId?: string;
  cropTypeId?: string;
  source?: SowingBreakdownSource;
}

export interface SowingBreakdownRow {
  sowingInstructionId: string;
  customerName: string;
  source: string;
  cropTypeName: string;
  variety?: string | null;
  trayType?: string | null;
  dateSowed: string;
  seedlingDate?: string | null;
  quantity?: number | null;
  quantityUnit?: string | null;
  amount?: number | null;
  amountPaid?: number | null;
  balancePayable?: number | null;
  orderNumber?: string | null;
}

export interface SowingBreakdownPagedResponse {
  items: SowingBreakdownRow[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
}

export interface SowingBreakdownExportFile {
  blob: Blob;
  fileName: string;
}

const buildParams = (
  filters: SowingBreakdownFilters,
  pageNumber?: number,
  pageSize?: number
) => {
  const params: Record<string, string> = {};
  if (filters.startDate) params.startDate = filters.startDate;
  if (filters.endDate) params.endDate = filters.endDate;
  if (filters.customerId) params.customerId = filters.customerId;
  if (filters.cropTypeId) params.cropTypeId = filters.cropTypeId;
  if (filters.source) params.source = filters.source;
  if (pageNumber !== undefined) params.pageNumber = String(pageNumber);
  if (pageSize !== undefined) params.pageSize = String(pageSize);
  return params;
};

const unwrapResponse = <T>(data: unknown): T => {
  if (data && typeof data === "object" && "data" in (data as Record<string, unknown>)) {
    return (data as { data: T }).data;
  }
  return data as T;
};

const parseFileNameFromDisposition = (contentDisposition?: string) => {
  if (!contentDisposition) return undefined;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) return decodeURIComponent(utf8Match[1]);

  const asciiMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return asciiMatch?.[1];
};

export const sowingBreakdownReportApi = {
  getSowingBreakdownReport: async (
    filters: SowingBreakdownFilters,
    pageNumber: number,
    pageSize: number
  ): Promise<SowingBreakdownPagedResponse> => {
    const response = await apiClient.get("/production/reports/sowing-breakdown", {
      params: buildParams(filters, pageNumber, pageSize),
    });
    return unwrapResponse<SowingBreakdownPagedResponse>(response.data);
  },

  exportSowingBreakdownReportExcel: async (
    filters: SowingBreakdownFilters
  ): Promise<SowingBreakdownExportFile> => {
    const response = await apiClient.get<Blob>(
      "/production/reports/sowing-breakdown/export",
      {
        params: buildParams(filters),
        responseType: "blob",
      }
    );
    const fileName =
      parseFileNameFromDisposition(response.headers["content-disposition"]) ||
      "sowing-breakdown-report.xlsx";
    return { blob: response.data, fileName };
  },

  exportSowingBreakdownReportPdf: async (
    filters: SowingBreakdownFilters
  ): Promise<SowingBreakdownExportFile> => {
    const response = await apiClient.get<Blob>(
      "/production/reports/sowing-breakdown/export/pdf",
      {
        params: buildParams(filters),
        responseType: "blob",
      }
    );
    const fileName =
      parseFileNameFromDisposition(response.headers["content-disposition"]) ||
      "sowing-breakdown-report.pdf";
    return { blob: response.data, fileName };
  },
};
